// Where a game's ending starts: the one rule every stage boundary, card and
// fact reads (api-review's phases provider calls `endgameStartOf` when a
// game opens). Two popular criteria, either of which makes a position an
// endgame (2026-10-10):
//
// - Speelman's: each side has at most 13 points of material (queen 9, rook
//   5, bishop and knight 3), the king excluded. Applied to the pieces only:
//   pawns are not counted, so a long pawn chain never delays the ending.
// - Lichess's: at most 6 major and minor pieces on the board in total.
import type { Fen } from '@chess/api-model';

/**
 * Speelman's limit: a position where each side's non-pawn material (queen
 * 9, rook 5, bishop and knight 3) is at most this is an endgame, queens on
 * or off. Jonathan Speelman, "Endgame Preparation" (1981).
 */
export const ENDGAME_MATERIAL = 13;

/**
 * Lichess's count: a position with at most this many major and minor pieces
 * (queens, rooks, bishops, knights) on the board, both sides together, is an
 * endgame (Lichess's game-phase division counts fewer than 7).
 */
export const ENDGAME_PIECES = 6;

const VALUE: Record<string, number> = { q: 9, r: 5, b: 3, n: 3 };

/** The board's major and minor pieces: their count, and each side's points. */
function pieces(board: string): { count: number; white: number; black: number } {
  let count = 0;
  let white = 0;
  let black = 0;
  for (const ch of board) {
    const value = VALUE[ch.toLowerCase()];
    if (value === undefined) continue;
    count++;
    if (ch >= 'A' && ch <= 'Z') white += value;
    else black += value;
  }
  return { count, white, black };
}

/**
 * An endgame position: either each side has at most `ENDGAME_MATERIAL`
 * points of non-pawn material (Speelman's 13, with or without a queen: a
 * queen and a minor piece qualify, two rooks and two minor pieces do not),
 * or the board holds at most `ENDGAME_PIECES` major and minor pieces in all
 * (Lichess's count: a queen and a rook each, nothing else, qualifies). Kings
 * and pawns never count.
 */
export function isEndgame(fen: Fen): boolean {
  const board = fen.split(' ')[0] ?? '';
  const { count, white, black } = pieces(board);
  if (count <= ENDGAME_PIECES) return true;
  return white <= ENDGAME_MATERIAL && black <= ENDGAME_MATERIAL;
}

/** The first ply whose position is an endgame, or undefined. Ply 0 is the start, which never is. */
export function endgameStartOf(positions: ReadonlyArray<Fen>): number | undefined {
  for (let ply = 1; ply < positions.length; ply++) {
    if (isEndgame(positions[ply])) return ply;
  }
  return undefined;
}
